import { useEffect, useMemo, useState } from "react";
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { CaretLeft, CaretRight, ChartBar } from "@phosphor-icons/react";
import api from "@/lib/api";

const MONTHS = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const money = (n) => `MX$${Math.abs(Number(n || 0)).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const compact = (n) => (Math.abs(n) >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : Math.abs(n) >= 1e3 ? `${(n / 1e3).toFixed(Math.abs(n) < 1e4 ? 1 : 0).replace(/\.0$/, "")}k` : String(Math.round(n)));
const PALETTE = ["#00873D", "#E0000F", "#F59E0B", "#7E57C2", "#0891B2", "#DB2777", "#65A30D", "#475569"];
const MAX_CATS = 6;

function TooltipBox({ active, payload, categories }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  const parts = Object.entries(d.by_category || {}).map(([id, v]) => ({ ...(categories.find((c) => c.id === id) || { name: "Otros", color: "#71717a" }), v })).sort((a, b) => b.v - a.v);
  return (
    <div className="bg-white border border-zinc-200 shadow-lg p-3 text-xs space-y-1 min-w-[200px]" data-testid="expenses-tooltip">
      <div className="font-mono-data tracking-widest text-zinc-500 uppercase">{MONTHS[d.month - 1]} · {d.count} gasto{d.count === 1 ? "" : "s"}</div>
      <div className="font-heading text-lg font-extrabold text-[#E0000F] numeric">{money(d.total_mxn)}</div>
      {parts.slice(0, 6).map((p) => (
        <div key={p.name} className="flex justify-between gap-3 text-zinc-600"><span className="inline-flex items-center gap-1.5 truncate"><span className="w-2 h-2 rounded-full shrink-0" style={{ background: p.color }} />{p.name}</span><b className="numeric">{money(p.v)}</b></div>
      ))}
      <div className="border-t border-zinc-100 pt-1 flex justify-between"><span className="text-zinc-500">Acumulado del año</span><b className="numeric text-zinc-900">{money(d.cumulative)}</b></div>
    </div>
  );
}

// Gastos por mes (ene→dic) del año, barras apiladas por categoría + línea de acumulado anual.
export default function ExpensesYearChart({ categoryId, refreshKey, onFx }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/expenses-history", { params: { year, category_id: categoryId || "all" } })
      .then((r) => { if (alive) { setData(r.data); setError(false); onFx?.(r.data.fx); } })
      .catch(() => alive && setError(true));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, categoryId, refreshKey]);

  // Colores únicos: si dos categorías comparten color, la segunda toma uno de la paleta
  const topCats = useMemo(() => {
    const used = new Set();
    return (data?.categories || []).slice(0, MAX_CATS).map((c) => {
      let color = (c.color || "").toLowerCase();
      if (!color || used.has(color)) color = PALETTE.find((p) => !used.has(p.toLowerCase())) || c.color;
      used.add(color.toLowerCase());
      return { ...c, color };
    });
  }, [data]);
  const rows = useMemo(() => {
    let acc = 0;
    return (data?.months || []).map((m) => {
      acc += m.total_mxn;
      const known = !m.future || m.total_mxn > 0;
      const row = { ...m, known, label: MONTHS[m.month - 1], cumulative: known ? acc : null, otros: 0 };
      Object.entries(m.by_category || {}).forEach(([id, v]) => { if (topCats.some((c) => c.id === id)) row[`c_${id}`] = v; else row.otros += v; });
      return row;
    });
  }, [data, topCats]);
  const past = rows.filter((r) => r.known);
  const withSpend = past.filter((r) => r.total_mxn > 0);
  const totalYear = past.reduce((s, r) => s + r.total_mxn, 0);
  const avg = withSpend.length ? totalYear / withSpend.length : 0;
  const top = withSpend.length ? withSpend.reduce((a, b) => (b.total_mxn > a.total_mxn ? b : a)) : null;
  const current = past[past.length - 1];
  const vsAvg = current && avg > 0 && withSpend.length > 1 ? ((current.total_mxn - avg) / avg) * 100 : null;
  const years = data?.years?.length ? data.years : [year];
  const canPrev = year > years[0], canNext = year < new Date().getFullYear();
  const hasOtros = rows.some((r) => r.otros > 0);

  return (
    <section className="border border-zinc-200 bg-white p-5" data-testid="expenses-year">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase font-mono-data tracking-wider text-zinc-500 inline-flex items-center gap-1.5"><ChartBar size={13} weight="bold" /> Gastos del año</div>
          <div className="text-sm text-zinc-500 mt-0.5">Cuánto gastaste cada mes, por categoría (dólares al TC {Number(data?.fx || 0).toFixed(2)})</div>
        </div>
        <div className="inline-flex items-center border border-zinc-200 rounded-sm" data-testid="expenses-year-nav">
          <button type="button" disabled={!canPrev} onClick={() => setYear((y) => y - 1)} className="px-2 py-1.5 hover:bg-zinc-50 disabled:opacity-30" data-testid="expenses-prev-year"><CaretLeft size={14} weight="bold" /></button>
          <span className="font-mono-data font-bold text-sm px-3 tabular-nums" data-testid="expenses-year-label">{year}</span>
          <button type="button" disabled={!canNext} onClick={() => setYear((y) => y + 1)} className="px-2 py-1.5 hover:bg-zinc-50 disabled:opacity-30" data-testid="expenses-next-year"><CaretRight size={14} weight="bold" /></button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
        <Stat label={`Total ${year}`} value={money(totalYear)} accent testid="expenses-total-year" />
        <Stat label="Promedio mensual" value={withSpend.length ? money(avg) : "—"} testid="expenses-avg" />
        <Stat label="Mes más alto" value={top ? money(top.total_mxn) : "—"} sub={top ? MONTHS[top.month - 1] : null} testid="expenses-top-month" />
        <Stat
          label={current ? `${MONTHS[current.month - 1]} vs promedio` : "Mes actual"}
          value={vsAvg == null ? "—" : `${vsAvg >= 0 ? "+" : "−"}${Math.abs(vsAvg).toFixed(0)}%`}
          tone={vsAvg == null ? null : vsAvg > 0 ? "bad" : "good"}
          sub={current ? money(current.total_mxn) : null}
          testid="expenses-vs-avg"
        />
      </div>

      <div className="mt-4 h-[260px]" data-testid="expenses-chart">
        {error && !data ? (
          <div className="h-full dotted-bg flex items-center justify-center text-sm text-[#E0000F]" data-testid="expenses-error">No se pudo cargar el historial. Intenta de nuevo.</div>
        ) : !data ? (
          <div className="h-full dotted-bg flex items-center justify-center text-sm text-zinc-400">Cargando…</div>
        ) : withSpend.length === 0 ? (
          <div className="h-full dotted-bg flex items-center justify-center text-sm text-zinc-400">Sin gastos en {year}</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={rows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f4f4f5" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#71717a" }} axisLine={false} tickLine={false} />
              <YAxis yAxisId="m" tickFormatter={compact} tick={{ fontSize: 11, fill: "#71717a" }} axisLine={false} tickLine={false} width={48} />
              <YAxis yAxisId="acc" orientation="right" tickFormatter={compact} tick={{ fontSize: 11, fill: "#a1a1aa" }} axisLine={false} tickLine={false} width={48} />
              <Tooltip content={<TooltipBox categories={[...topCats, ...(data.categories || []).filter((c) => !topCats.some((t) => t.id === c.id))]} />} cursor={{ fill: "#f4f4f5" }} />
              {topCats.map((c, i) => <Bar key={c.id} yAxisId="m" dataKey={`c_${c.id}`} stackId="g" fill={c.color} radius={i === topCats.length - 1 && !hasOtros ? [3, 3, 0, 0] : 0} barSize={22} />)}
              {hasOtros && <Bar yAxisId="m" dataKey="otros" stackId="g" fill="#a1a1aa" radius={[3, 3, 0, 0]} barSize={22} />}
              <Line yAxisId="acc" type="monotone" dataKey="cumulative" stroke="#002FA7" strokeWidth={2} dot={{ r: 2.5, fill: "#002FA7", strokeWidth: 0 }} activeDot={{ r: 4 }} connectNulls={false} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
      <div className="mt-2 flex items-center gap-x-4 gap-y-1 flex-wrap text-[11px] text-zinc-500">
        {topCats.map((c) => <span key={c.id} className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 inline-block rounded-sm" style={{ background: c.color }} /> {c.name}</span>)}
        {hasOtros && <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 inline-block rounded-sm bg-zinc-400" /> Otros</span>}
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-0.5 bg-[#002FA7] inline-block rounded" /> Acumulado del año</span>
      </div>
    </section>
  );
}

function Stat({ label, value, sub, tone, accent, testid }) {
  const color = tone === "bad" ? "text-[#E0000F]" : tone === "good" ? "text-[#00873D]" : accent ? "text-[#E0000F]" : "text-zinc-900";
  return (
    <div className="border border-zinc-100 bg-zinc-50/60 p-3" data-testid={testid}>
      <div className="text-[10px] uppercase font-mono-data tracking-wider text-zinc-500">{label}</div>
      <div className={`numeric text-lg font-extrabold mt-0.5 ${color}`}>{value}</div>
      {sub && <div className="text-[11px] font-semibold text-zinc-500">{sub}</div>}
    </div>
  );
}
